// ---------------------------------------------------------------------------
// Restaurant image upload
// ---------------------------------------------------------------------------
// storage.rules only requires an authenticated user (no ownerId/status check),
// so an "unauthorized" failure here means the client's ID token was stale at
// the moment of upload (e.g. right after switching accounts in the same
// browser) rather than a real permissions problem. Force a token refresh and
// retry once before giving up.
// ---------------------------------------------------------------------------

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { downscaleImage } from './imageResize';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
// storage.rules rejects anything at or above 5 MB, so this is the ceiling on
// what may actually be uploaded.
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
// What an owner is allowed to *pick*. Larger than the upload ceiling on
// purpose: downscaling reliably brings a phone photo far under it, so
// rejecting a 9 MB original outright — which is what used to happen — turned
// an ordinary camera roll picture into a dead end during sign-up.
const MAX_SOURCE_BYTES = 32 * 1024 * 1024;

function uploadError(message, code = 'storage/invalid-file') {
  const err = new Error(message);
  err.code = code;
  err.isUploadError = true;
  return err;
}

// Validates, downscales and uploads an image under `${folder}/${uid}/`,
// returning the storage reference.
async function uploadImageTo(storage, auth, imageFile, folder) {
  const user = auth.currentUser;
  if (!user) {
    throw uploadError('You must be signed in to upload an image.', 'storage/unauthorized');
  }
  if (!ACCEPTED_TYPES.includes(imageFile.type)) {
    throw uploadError('Image must be a JPEG, PNG, or WebP.');
  }
  if (imageFile.size >= MAX_SOURCE_BYTES) {
    throw uploadError('That image is too large to process. Please choose one under 32 MB.');
  }

  // Shrink before upload: the owner's original never leaves their device, so
  // the upload is quicker for them and every visitor downloads a fraction of
  // the bytes. Falls back to the original if the browser can't re-encode it.
  const file = await downscaleImage(imageFile);

  if (file.size >= MAX_UPLOAD_BYTES) {
    throw uploadError('Image must be under 5 MB. Please choose a smaller one.');
  }

  await user.getIdToken();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const storageRef = ref(storage, `${folder}/${user.uid}/${Date.now()}_${safeName}`);
  const metadata = { contentType: file.type };
  try {
    await uploadBytes(storageRef, file, metadata);
  } catch (err) {
    if (err.code === 'storage/unauthorized') {
      await user.getIdToken(true);
      try {
        await uploadBytes(storageRef, file, metadata);
      } catch (retryErr) {
        retryErr.isUploadError = true;
        throw retryErr;
      }
    } else {
      err.isUploadError = true;
      throw err;
    }
  }
  return storageRef;
}

export async function uploadRestaurantImage(storage, auth, imageFile) {
  const storageRef = await uploadImageTo(storage, auth, imageFile, 'businesses');
  return getDownloadURL(storageRef);
}

// Proof for a "Claim this restaurant" request (a licence, a utility bill, a
// photo behind the counter). Private: storage.rules lets only the uploader
// and admins read claims/, and the claim stores the path, not a public
// download link — so a proof document never has a shareable URL.
export async function uploadClaimProof(storage, auth, imageFile) {
  if (imageFile.type === 'application/pdf') {
    const user = auth.currentUser;
    if (!user) throw uploadError('Sign in to upload proof.', 'storage/unauthorized');
    if (imageFile.size >= MAX_UPLOAD_BYTES) throw uploadError('PDF must be under 5 MB.');
    await user.getIdToken(true);
    const storageRef = ref(storage, `businessClaims/${user.uid}/${Date.now()}.pdf`);
    await uploadBytes(storageRef, imageFile, { contentType: 'application/pdf' });
    return storageRef.fullPath;
  }
  const storageRef = await uploadImageTo(storage, auth, imageFile, 'businessClaims');
  return storageRef.fullPath;
}

export function friendlyUploadError(err) {
  if (err?.code === 'storage/invalid-file') return err.message;
  if (err?.code === 'storage/unauthorized') {
    return "We couldn't upload your image because your session expired. Sign in again and retry.";
  }
  return "We couldn't upload your image — please check your connection and try again.";
}

// Removes an image that was uploaded for a submission that then failed.
//
// The listing photo is uploaded before submitRestaurantListing is called, so
// when that call is refused — most often "you already have a listing at this
// address" — the file stayed in Storage attached to nothing, and nothing ever
// cleaned it up. Best-effort by design: the submission has already failed and
// the owner is being shown why, so a failure to tidy up must not replace that
// message with a storage error.
export async function discardUploadedImage(storage, url) {
  if (!url) return;
  try {
    await deleteObject(ref(storage, url));
  } catch {
    /* already gone, or not ours to delete — nothing useful to do here */
  }
}
